<template>
  <div id="app"
       class="app"
       :class="[className, getPlatformInfo]">
    <alert-sticky-bar/>
    <transition name="layout">
      <component class="layout"
                 :is="layoutName"
                 @click.native="hideAllLayers"/>
    </transition>
    <dialogs-wrapper tag="div"></dialogs-wrapper>
    <dialogs-wrapper tag="div" name="error"></dialogs-wrapper>
    <transition name="error">
      <error404 v-if="hasError === 404" />
      <error500 v-else-if="hasError === 500" />
    </transition>
  </div>
</template>

<script>
// must import app.scss first for correct css ordering
import '@/styles/app.scss'
import { SIGNIN_LINK } from '@/libs/constants'
import { mapState, mapMutations } from 'vuex'
import { create } from 'vue-modal-dialogs'
import { STORAGE_KEY, storageManager } from '@/libs/storage-manager'
import {
  getAuthToken,
  getAuthCookie,
  removeAuthData
} from '@/libs/auth-helper'
import { clearAllApiCache } from '@/libs/api-request'
import DefaultLayout from '@/layouts/DefaultLayout'
import ReportLayout from '@/layouts/ReportLayout'
import DashboardLayout from '@/layouts/DashboardLayout'
import PageLayout from '@/layouts/PageLayout'
import NoHeaderLayout from '@/layouts/NoHeaderLayout'
import EmptyLayout from '@/layouts/EmptyLayout'
import AppDialog from '@/components/ui/AppDialog'
import AlertStickyBar from '@/components/header/AlertStickyBar'
import { browserInfo } from '@/libs/utils'
import Error500 from '@/components/errors/500.vue'
import Error404 from '@/components/errors/404.vue'
import MineLayout from '@/layouts/MineLayout.vue'

export default {
  name: 'App',
  components: {
    Error404,
    Error500,
    AlertStickyBar,
    AppDialog,
    'default-layout': DefaultLayout,
    'report-layout': ReportLayout,
    'dashboard-layout': DashboardLayout,
    'page-layout': PageLayout,
    'no-header-layout': NoHeaderLayout,
    'empty-layout': EmptyLayout,
    'mine-layout': MineLayout
  },
  data () {
    return {
      hasError: false // prevent multiple error dialog
    }
  },
  computed: {
    ...mapState([
      'userData',
      'errorData',
      'isDashboardEditMode',
      'stickyDataId',
      'showStickyData',
      'myInfoTimestamp',
      'showNoPvDialog',
      'dreamVersion'
    ]),
    getPlatformInfo () {
      return 'platform__' + browserInfo.platform
    },
    layoutName () {
      if (!this.$route.matched.length) {
        return null
      }
      const route = Array.from(this.$route.matched).reverse()
        .find(item => item.meta.hasOwnProperty('layout'))

      const layout = route ? route.meta.layout : 'default'
      return `${layout}-layout`
    },
    className () {
      const items = this.$route ? [`route-${this.$route.name}`] : ''

      if (this.$route.meta.appClass) {
        items.push(this.$route.meta.appClass)
      }

      if (items.length) {
        return items.join(' ')
      }
      return null
    }
  },
  watch: {
    $route: {
      handler (current, prev) {
        // this.syncAuthTokenAndCookie()
        const guestOnly = current.meta.guestOnly
        const authToken = getAuthToken()

        if (guestOnly) {
          if (authToken) {
            this.$router.replace({ name: 'dashboard' })
          }
        } else {
          if (!authToken) {
            this.signOut()
            window.location.href = SIGNIN_LINK
          }
        }
      },
      immediate: true
    },
    errorData (value) {
      if (value.error && !this.hasError) {
        setTimeout(() => {
          this.hasError = value.code
        }, 500)
      } else {
        this.hasError = false
      }
    },
    dreamVersion (current, prev) {
      if (current && prev && current > prev) {
        this.$nextTick().then(() => {
          window.location.reload(true)
        })
      }
    }
  },
  created () {
    // this.syncTimer = window.setInterval(() => {
    //   this.syncAuthTokenAndCookie()
    // }, 1000)
    window.addEventListener('beforeunload', event => {
      if (this.$route.name === 'dashboard' && this.isDashboardEditMode) {
        event.preventDefault()
        event.returnValue = this.$t('common.windowUnload')
        // todo: check IE
        return event.returnValue
      }

      if (this.userData) {
        storageManager.setItem(STORAGE_KEY.showNoPvDialog, this.showNoPvDialog)
        storageManager.setItem(STORAGE_KEY.stickyDataId, this.stickyDataId)
        storageManager.setItem(STORAGE_KEY.showStickyData, this.showStickyData)
        storageManager.setItem(STORAGE_KEY.myInfoTimestamp,
          this.myInfoTimestamp)
      }
    })
  },
  destroyed () {
    window.clearInterval(this.syncTimer)
  },
  methods: {
    ...mapMutations([
      'setShowUserMenu',
      'setShowReportPageList',
      'setErrorData',
      'clearAllUserData'
    ]),
    // to-do: use single mutation
    hideAllLayers () {
      this.setShowUserMenu(false)
      this.setShowReportPageList(false)
    },
    signOut () {
      if (this.userData) {
        storageManager.initUser(this.userData.userNum)
        storageManager.removeItem(STORAGE_KEY.showNoPvDialog)
        storageManager.removeItem(STORAGE_KEY.stickyDataId)
        storageManager.removeItem(STORAGE_KEY.showStickyData)
        storageManager.removeItem(STORAGE_KEY.myInfoTimestamp)
      }

      clearAllApiCache()
      this.clearAllUserData()
      removeAuthData()
    }
  }
}
</script>

<style lang="scss" scoped>
.layout {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;

  transition: opacity 0.3s ease;
  opacity: 1;

  & /deep/ .layout-transition-up {
    transition: margin-bottom 0.3s ease, transform 0.5s ease, opacity 0.5s ease;
  }

  & /deep/ .layout-transition-left {
    transition: transform 0.5s ease, opacity 0.5s ease;
  }

  & /deep/ .layout-transition-fade {
    transition: height 0.3s ease, opacity 0.5s ease;
  }

  &.layout-enter,
  &.layout-leave-to {
    & /deep/ .layout-transition-up {
      opacity: 0;
      transform: translateY(-200px);
    }

    & /deep/ .layout-transition-left {
      opacity: 0;
      transform: translateX(-200px);
    }

    & /deep/ .layout-transition-fade {
      opacity: 0;
    }
  }
}

.error-leave-active,
.error-enter-active {
  transition: transform 0.5s ease;
  transform: translateY(0);
  opacity: 1;
}

.error-enter,
.error-leave-to {
  transform: translateY(-100%);
  opacity: 0;
}
</style>
